import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const StartupPortfolioCard = ({
    startup,
    investment = null,
    showInvestmentDetails = true,
    onClick = null,
    variant = 'default' // 'default', 'compact', 'detailed'
}) => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const formatCurrency = (amount, currency = 'INR') => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'EXITED':
                return 'bg-gray-100 text-gray-800 border-gray-200';
            case 'PENDING':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            default:
                return 'bg-blue-100 text-blue-800 border-blue-200';
        }
    };

    const getStageColor = (stage) => {
        switch (stage) {
            case 'SEED':
                return 'bg-purple-100 text-purple-700';
            case 'SERIES_A':
                return 'bg-blue-100 text-blue-700';
            case 'SERIES_B':
                return 'bg-indigo-100 text-indigo-700';
            case 'SERIES_C':
                return 'bg-teal-100 text-teal-700';
            case 'GROWTH':
                return 'bg-green-100 text-green-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const handleClick = () => {
        if (onClick) {
            onClick(startup);
        } else {
            navigate(`/${user?.tenantIdentifier || 'default'}/investor/startups/${startup.id}`);
        }
    };

    if (variant === 'compact') {
        return (
            <div
                onClick={handleClick}
                className="group bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg hover:border-blue-300 transition-all duration-300 cursor-pointer"
            >
                <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
                        <span className="text-white font-bold text-lg">
                            {(startup.organizationName || startup.name || 'S')?.charAt(0)?.toUpperCase()}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                            {startup.organizationName || startup.name}
                        </h3>
                        <p className="text-sm text-gray-500 truncate">{startup.department || startup.industry || 'Technology'}</p>
                    </div>
                    {investment && (
                        <div className="text-right flex-shrink-0">
                            <p className="font-semibold text-gray-900">{formatCurrency(investment.investmentAmount)}</p>
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(investment.status)}`}>
                                {investment.status || 'ACTIVE'}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            onClick={handleClick}
            className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl hover:border-blue-300 transition-all duration-300 cursor-pointer"
        >
            {/* Header with solid color */}
            <div className="h-24 bg-blue-600 relative">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="absolute -bottom-8 left-6">
                    <div className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center">
                        <span className="text-2xl font-bold text-blue-600">
                            {(startup.organizationName || startup.name || 'S')?.charAt(0)?.toUpperCase()}
                        </span>
                    </div>
                </div>
                {investment?.status && (
                    <div className="absolute top-4 right-4">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(investment.status)} backdrop-blur-sm`}>
                            {investment.status}
                        </span>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="pt-12 px-6 pb-6">
                <div className="mb-4">
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {startup.organizationName || startup.name}
                    </h3>
                    <p className="text-sm text-gray-500">{startup.email}</p>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {startup.department && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            {startup.department}
                        </span>
                    )}
                    {investment?.investmentStage && (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${getStageColor(investment.investmentStage)}`}>
                            {investment.investmentStage.replace('_', ' ')}
                        </span>
                    )}
                </div>

                {/* Investment Details */}
                {showInvestmentDetails && investment && (
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">Investment</span>
                            <span className="text-lg font-bold text-gray-900">
                                {formatCurrency(investment.investmentAmount, investment.currency)}
                            </span>
                        </div>
                        {investment.equityPercentage && (
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">Equity</span>
                                <span className="font-semibold text-gray-700">{investment.equityPercentage}%</span>
                            </div>
                        )}
                        {investment.investmentDate && (
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">Date</span>
                                <span className="text-sm text-gray-700">
                                    {new Date(investment.investmentDate).toLocaleDateString('en-IN', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                    })}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Quick Stats for detailed variant */}
                {variant === 'detailed' && (
                    <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-100">
                        <div className="text-center">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Files</p>
                            <p className="text-lg font-bold text-gray-900">12</p>
                        </div>
                        <div className="text-center border-x border-gray-100">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Events</p>
                            <p className="text-lg font-bold text-gray-900">3</p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Returns</p>
                            <p className="text-lg font-bold text-green-600">+24%</p>
                        </div>
                    </div>
                )}

                {/* Action hint */}
                <div className="mt-4 flex items-center justify-end text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-sm font-medium">View Details</span>
                    <svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default StartupPortfolioCard;
